import { z } from 'zod';
import { MoneySchema } from './common.js';
// Prisma Decimal columns serialize as canonical strings; allow either string
// or non-negative number on the wire (matches the convention used in ar.ts /
// lva.ts / loan.ts).
const DecimalLike = z.union([MoneySchema, z.coerce.number().nonnegative()]);
// ============================================================
// AdvanceStatus enum — mirrors apps/api/prisma enum AdvanceStatus
// ============================================================
export const AdvanceStatusSchema = z.enum([
    'UNISSUED',
    'OUTSTANDING',
    'PARTIALLY_CLEARED',
    'CLEARED',
    'WRITTEN_OFF',
]);
// ============================================================
// AdvancePurposeCategory — 8 codes per spec §25.
// Validated as enum at the controller boundary so a typo client-side surfaces
// as a 400 rather than landing in the DB.
// ============================================================
export const AdvancePurposeCategorySchema = z.enum([
    'TRAVEL_LOCAL',
    'TRAVEL_INTL',
    'SITE_VISIT',
    'CLIENT_ENT',
    'TRAINING',
    'EXHIBITION',
    'PURCHASE',
    'OTHER',
]);
// ============================================================
// AdvanceClearanceLine — single expense receipt within a clearance
// ============================================================
export const AdvanceClearanceLineSchema = z.object({
    id: z.uuid(),
    clearanceId: z.uuid(),
    description: z.string().min(1),
    // 5xxx expense COA code — service enforces it exists in this company's COA.
    expenseAccountCode: z.string().min(1),
    amount: DecimalLike,
    receiptRef: z.string().nullable().optional(),
});
// Wire shape for the line on POST — id / clearanceId are server-derived.
// M3 — tax-aware clearance: when the tax fields are omitted the server computes
// them from the line's resolved AdvanceExpenseCategory; when present they are
// treated as an audited manual override (taxOverridden flag).
export const CreateAdvanceClearanceLineSchema = z.object({
    description: z.string().min(1),
    expenseAccountCode: z.string().min(1),
    amount: z.coerce.number().positive(),
    receiptRef: z.string().optional(),
    // M3 — expense category + tax capture
    categoryId: z.string().uuid().optional(),
    documentType: z.enum(['TAX_INVOICE_FULL', 'RECEIPT', 'PETTY_VOUCHER', 'NONE']).default('NONE'),
    vendorTaxId: z.string().optional(),
    vendorName: z.string().optional(),
    // tax — when omitted, server computes from category; when present, treated as an audited manual override
    inputVatDeductible: z.enum(['CREDITABLE', 'FORBIDDEN', 'NONE']).optional(),
    vatBase: z.coerce.number().nonnegative().optional(),
    vatAmount: z.coerce.number().nonnegative().optional(),
    whtTypeCode: z.string().optional(),
    whtBase: z.coerce.number().nonnegative().optional(),
    whtAmount: z.coerce.number().nonnegative().optional(),
    whtFormCode: z.enum(['PND3', 'PND53']).optional(),
    // M4 — the MinIO storage key of the uploaded receipt backing this line. Carried
    // from POST :id/receipts into clearAdvance; the ≥500 / creditable-VAT gates check
    // its presence at clear time, and it is persisted on the clearance line.
    attachmentStorageKey: z.string().optional(),
    // M5 — line discriminator. RECEIPT_EXPENSE = today's tax-aware receipt path
    // (computeLineTax + attachment/dedup gates). PER_DIEM / MILEAGE = a non-receipt
    // allowance line: allowance = perDiemRate × perDiemQty (qty = days or km),
    // expense account from the chosen PerDiemRate.glExpenseCode, NO VAT/WHT, NO
    // attachment gate, NO dedup. perDiemRateId resolves the master row server-side;
    // perDiemRate/perDiemQty carry the (possibly edited) claimed rate + quantity.
    lineType: z.enum(['RECEIPT_EXPENSE', 'PER_DIEM', 'MILEAGE']).default('RECEIPT_EXPENSE'),
    perDiemRateId: z.string().uuid().optional(),
    perDiemQty: z.coerce.number().nonnegative().optional(),
    perDiemRate: z.coerce.number().nonnegative().optional(),
    // M5 — receipt dedup escape. dupSeq 0 = normal; >0 = a deliberate legitimate
    // re-submit of the same logical receipt, which MUST carry an audited
    // dupOverrideReason (server-enforced). Mirrors the ApBill dedup pattern.
    dupSeq: z.coerce.number().int().min(0).default(0),
    dupOverrideReason: z.string().max(200).optional(),
});
// ============================================================
// M4 — receipt upload result (POST :id/receipts). The uploaded file is stored in
// MinIO (storageKey) and run through Claude OCR; `suggested` carries the prefill
// the clearance modal applies to the line (numbers coerced from the extracted
// string fields; documentType heuristic full-tax-invoice vs receipt vs none).
// ============================================================
export const AdvanceReceiptUploadResultSchema = z.object({
    storageKey: z.string(),
    fileName: z.string(),
    suggested: z.object({
        amount: z.coerce.number().nullable(),
        documentDate: z.string().nullable(),
        vendorTaxId: z.string().nullable(),
        vatAmount: z.coerce.number().nullable(),
        documentType: z.enum(['TAX_INVOICE_FULL', 'RECEIPT', 'PETTY_VOUCHER', 'NONE']),
    }),
});
// ============================================================
// M4 — soft create precheck (GET advance-precheck?employeeId=&amount=). A purely
// advisory warning surface: does the employee already hold an open advance, and
// does the requested amount exceed their per-employee advanceLimit? It NEVER
// blocks — the web renders a banner and submit still works. `advanceLimit` is
// null when the employee has no configured cap.
// ============================================================
export const AdvancePrecheckResultSchema = z.object({
    hasOpenAdvance: z.boolean(),
    openCount: z.number().int(),
    exceedsLimit: z.boolean(),
    advanceLimit: z.coerce.number().nullable(),
});
// ============================================================
// M4 — overdue dunning manual remind (POST :id/remind). The optional `note` is
// appended to the Thai reminder body. Sending goes through the pluggable
// MailerService (log/stub now); the same path the BullMQ dunning cron uses. The
// employee always receives it; when the advance is > 7 days overdue the finance
// notify mailbox (env ADVANCE_FINANCE_NOTIFY_EMAIL) is added too (escalation).
// ============================================================
export const RemindAdvanceSchema = z.object({
    note: z.string().max(500).optional(),
});
export const RemindAdvanceResultSchema = z.object({
    sent: z.boolean(),
    to: z.array(z.string()),
    daysOverdue: z.number().int(),
});
// ============================================================
// M5 — bulk overdue remind (POST bulk-remind). The list's functional row-select
// hands the chosen advance ids here; the controller fans out remindAdvance per id
// and collects a per-id result (one non-overdue/settled id must NOT abort the
// batch). Capped at 50 to bound a single request AND to limit mail amplification:
// with the current MailerService stub the fan-out is harmless, but a real provider
// could blast up to 2 emails per id (employee + finance escalation) — see the
// chunking note on the controller's bulkRemind handler.
// ============================================================
export const BulkRemindSchema = z.object({
    ids: z.array(z.string().uuid()).min(1).max(50),
});
// One row of the bulk-remind result — either a sent reminder or the captured
// error (e.g. "ยังไม่เกินกำหนด") for an id the per-id remind guard rejected.
export const BulkRemindResultRowSchema = z.object({
    id: z.string(),
    sent: z.boolean().optional(),
    to: z.array(z.string()).optional(),
    daysOverdue: z.number().int().optional(),
    error: z.string().optional(),
});
// ============================================================
// AdvanceExpenseCategory — master driving default VAT/WHT treatment for a
// clearance line (mirrors apps/api prisma model advance_expense_categories).
// Decimal columns (whtRate) serialize as strings; coerce to number on the wire.
// ============================================================
export const AdvanceExpenseCategorySchema = z.object({
    id: z.string(),
    companyId: z.string(),
    code: z.string(),
    nameTh: z.string(),
    nameEn: z.string().nullable().optional(),
    glExpenseCode: z.string(),
    vatApplicable: z.boolean(),
    whtTypeCode: z.string().nullable().optional(),
    whtRate: z.coerce.number().nullable().optional(),
    whtFormCode: z.string().nullable().optional(),
    isPerDiem: z.boolean(),
    isActive: z.boolean(),
});
// ============================================================
// PerDiemRate — per-company per-diem / mileage allowance master (M5). Mirrors
// apps/api prisma model per_diem_rates. A PER_DIEM/MILEAGE clearance line picks a
// row; allowance = rate × qty, expense to glExpenseCode, no VAT/WHT. ceilingRate
// is the civil-service safe-harbour: any excess over it is flagged taxable
// (ภ.ง.ด.1) but NOT withheld at clearance. Decimal columns serialize as strings;
// coerce to number on the wire.
// ============================================================
export const PerDiemRateSchema = z.object({
    id: z.string(),
    companyId: z.string(),
    code: z.string(),
    nameTh: z.string(),
    rateType: z.string(), // PER_DAY | PER_KM
    rate: z.coerce.number(),
    ceilingRate: z.coerce.number(),
    glExpenseCode: z.string(),
    isActive: z.boolean(),
});
// ============================================================
// PayrollDeductionRow — a row of the report-only payroll-deduction surface (M5).
// write-off flags the advance (pendingPayrollDeduction + pendingPayrollAmount =
// the written-off remaining the employee still owes); HR deducts it out-of-band.
// No JV beyond the write-off itself. writtenOffAt serializes as a string (null
// until written off).
// ============================================================
export const PayrollDeductionRowSchema = z.object({
    id: z.string(),
    voucherNo: z.string(),
    employeeName: z.string(),
    pendingPayrollAmount: z.coerce.number(),
    writtenOffAt: z.string().nullable(),
});
// ============================================================
// TaxablePerDiemRow — a row of the report-only taxable-per-diem surface (M5).
// A PER_DIEM / MILEAGE clearance line whose claimed rate exceeds the civil-service
// ceiling persists taxablePerDiemExcess (> 0); this report surfaces those excesses
// to HR for ภ.ง.ด.1 (the excess is flagged at clearance but NOT withheld). Lines
// with 0 / null excess do not appear. clearDate serializes as a string.
// ============================================================
export const TaxablePerDiemRowSchema = z.object({
    advanceVoucherNo: z.string(),
    employeeName: z.string(),
    description: z.string(),
    taxablePerDiemExcess: z.coerce.number(),
    clearDate: z.string(),
});
// ============================================================
// AdvanceClearance — header
// ============================================================
export const AdvanceClearanceSchema = z.object({
    id: z.uuid(),
    advanceId: z.uuid(),
    clearDate: z.coerce.date(),
    totalClearedExpense: DecimalLike,
    refundAmount: DecimalLike,
    journalEntryId: z.uuid().nullable().optional(),
    postedBy: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
});
export const AdvanceClearanceWithLinesSchema = AdvanceClearanceSchema.extend({
    lines: z.array(AdvanceClearanceLineSchema),
});
// ============================================================
// EmployeeAdvance — header
// ============================================================
export const EmployeeAdvanceSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    voucherNo: z.string().min(1),
    advanceDate: z.coerce.date(),
    employeeId: z.uuid(),
    purpose: z.string(),
    purposeCategory: AdvancePurposeCategorySchema,
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
    amount: DecimalLike,
    clearDeadlineDate: z.coerce.date(),
    status: AdvanceStatusSchema,
    totalClearedAmount: DecimalLike,
    refundAmount: DecimalLike,
    journalEntryIdIssue: z.uuid().nullable().optional(),
    // M2 — approval workflow (engine lane) + disbursement. Decoupled from `status`.
    approvalStatus: z.string().nullable().optional(),
    currentStep: z.number().int().default(0),
    submittedBy: z.string().nullable().optional(),
    submittedAt: z.coerce.date().nullable().optional(),
    approvedBy: z.string().nullable().optional(),
    approvedAt: z.coerce.date().nullable().optional(),
    rejectedReason: z.string().nullable().optional(),
    returnedReason: z.string().nullable().optional(),
    payMethod: z.string().nullable().optional(),
    disburseAccountCode: z.string().nullable().optional(),
    disbursedAt: z.coerce.date().nullable().optional(),
    disbursedBy: z.string().nullable().optional(),
    returnDate: z.coerce.date().nullable().optional(),
    postedBy: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// List-row shape — adds derived display fields the table needs without forcing
// the FE to do its own joins. `agingDays` is days since advanceDate, `overdue`
// is clearDeadlineDate < today AND status != CLEARED/WRITTEN_OFF.
export const EmployeeAdvanceListRowSchema = EmployeeAdvanceSchema.extend({
    employeeName: z.string(),
    employeeDept: z.string().nullable().optional(),
    agingDays: z.number().int(),
    overdue: z.boolean(),
    outstandingAmount: DecimalLike,
});
export const EmployeeAdvanceWithClearancesSchema = EmployeeAdvanceListRowSchema.extend({
    clearances: z.array(AdvanceClearanceWithLinesSchema),
});
// ============================================================
// CreateEmployeeAdvance — POST body
// voucherNo / status / totals are server-generated. cashAccountCode is optional;
// when omitted the service resolves the company's BANK_PRIMARY account. Override
// with a cash COA when paying by petty cash.
// ============================================================
export const CreateEmployeeAdvanceSchema = z.object({
    advanceDate: z.iso.date(),
    employeeId: z.uuid(),
    purpose: z.string().min(1),
    purposeCategory: AdvancePurposeCategorySchema,
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
    amount: z.coerce.number().positive(),
    clearDeadlineDate: z.iso.date(),
    // Reference date the employee returns from the mission (per-diem day-count in M5
    // + policy reporting). Optional.
    returnDate: z.iso.date().optional(),
    // Omit to use the company's BANK_PRIMARY account at disburse. Override with a cash
    // COA for petty-cash payouts (stored as disburseAccountCode).
    cashAccountCode: z.string().min(1).optional(),
});
// ============================================================
// ClearEmployeeAdvance — POST :id/clear body
//
// Validation invariants enforced server-side (Decimal-safe):
//   1. sum(lines.amount) + refundAmount  <=  (advance.amount - advance.totalClearedAmount - advance.refundAmount)
//      i.e. total clearance value cannot exceed the remaining outstanding balance.
//   2. Each line.expenseAccountCode must exist in the company's COA and start with '5' (expense).
//   3. refundAmount >= 0; when > 0 the JV adds a Dr <cashAccountCode> leg.
//
// When sum(lines)+refundAmount equals the *full* outstanding, status flips to
// CLEARED; otherwise PARTIALLY_CLEARED. The remaining outstanding is computed
// against (amount - previously cleared - previously refunded).
// ============================================================
export const ClearEmployeeAdvanceSchema = z.object({
    clearDate: z.iso.date(),
    lines: z.array(CreateAdvanceClearanceLineSchema).min(1),
    refundAmount: z.coerce.number().nonnegative().default(0),
    // Where the refund cash lands. Omit to use the company's BANK_PRIMARY account.
    // Ignored when refundAmount === 0.
    cashAccountCode: z.string().min(1).optional(),
});
// ============================================================
// WriteOffEmployeeAdvance — POST :id/write-off body
// Posts the residual outstanding as expense. writeOffAccountCode is optional;
// when omitted the service resolves the company's MISC_EXPENSE account. Can be
// overridden with any postable cost/expense COA in the company's chart.
// ============================================================
export const WriteOffEmployeeAdvanceSchema = z.object({
    writeOffDate: z.iso.date(),
    reason: z.string().min(1),
    writeOffAccountCode: z.string().min(1).optional(),
});
// ============================================================
// M2 — approval + disburse (central approval engine wiring)
// ============================================================
export const PayMethodSchema = z.enum(['TRANSFER', 'CASH', 'CHEQUE']);
// One materialized approval-chain row (mirrors PaymentRequestApprovalSchema).
export const EmployeeAdvanceApprovalSchema = z.object({
    id: z.uuid(),
    advanceId: z.uuid(),
    stepNo: z.number().int(),
    roleCode: z.string(),
    roleLabel: z.string(),
    approvedAt: z.coerce.date().nullable().optional(),
    approvedBy: z.string().nullable().optional(),
    comment: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
});
// findAdvance return after M2 — the with-clearances detail PLUS the materialized
// approval chain. Engine-lane fields (approvalStatus/currentStep/…) are inherited
// from the base EmployeeAdvanceSchema.
export const EmployeeAdvanceWithApprovalsSchema = EmployeeAdvanceWithClearancesSchema.extend({
    approvals: z.array(EmployeeAdvanceApprovalSchema),
});
// Disburse body — choose the pay method + (optional) cash account at payout.
export const DisburseAdvanceSchema = z.object({
    payMethod: PayMethodSchema.optional(),
    disburseAccountCode: z.string().min(1).optional(),
});
// Controller DTOs reuse the generic payreq step schemas ({step, comment?}/{step, reason}).
export { ApprovePaymentRequestSchema as ApproveAdvanceStepSchema, RejectPaymentRequestSchema as RejectAdvanceStepSchema, ReturnPaymentRequestSchema as ReturnAdvanceStepSchema, } from './payment-request.js';
//# sourceMappingURL=advance.js.map